import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { OverlayContainer } from '@angular/cdk/overlay';
import { UserGeography } from '../../shared/models/user-geography.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Geography } from '../../shared/models/geography.model';
import { ISelectedGeography } from '../../shared/interfaces/selected-geography.interface';
import {
  GEOGRAPHY_TOOLTIP_DEFAULT,
  GEOGRAPHY_TOOLTIP_MULTIPLE,
  GEOGRAPHY_TOOLTIP_SINGLE,
} from '../../shared/constants/geography-selector.constants';
import { GeographyDialogComponent } from './geography-dialog/geography-dialog';
import { ClaimStore } from '../../../../src/app/core/store/claim-store';
import { FavouriteGeography } from '../../shared/models/favourite-geography.response.model';
import { ClaimsService } from '../../core/services/claims.service';


@Component({
  selector: 'emma-geography-selector',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    GeographyDialogComponent,
    MatTooltipModule,
  ],
  templateUrl: './geography-selector.html',
  styleUrls: ['./geography-selector.scss'],
})
export class GeographySelectorComponent {
  @Input() public geographies: Array<UserGeography> = [];
  @Input() public projectGeographies: Array<Geography> = [];
  public selectedGeographies = signal<Array<ISelectedGeography>>([]);

  private readonly dialog = inject(MatDialog);
  private readonly store = inject(ClaimStore);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly favouriteGeographyService = inject(ClaimsService);
  private favouriteGeographies: Array<FavouriteGeography> = [];

  public selectedUserGeographies = signal<Array<UserGeography>>([]);
  public selectedProjectGeographies = signal<Array<Geography>>([]);

  constructor() {
    this.overlayContainer.getContainerElement().classList.add('e-selector-overlay');
  }

  public ngOnInit(): void {
    this.fetchFavouriteGeographies();
  }

  public geographyTooltip = computed(() => {
    const selected = this.selectedGeographies();
    if (!selected || selected.length === 0) {
      return GEOGRAPHY_TOOLTIP_DEFAULT;
    }
    if (selected.length === 1) {
      return GEOGRAPHY_TOOLTIP_SINGLE(selected[0].name);
    }
    return GEOGRAPHY_TOOLTIP_MULTIPLE;
  });

  public openDialog(): void {
    const dialogRef = this.dialog.open(GeographyDialogComponent, {
      panelClass: 'e-selector-postion-container',
      data: {
        geographies: this.geographies,
        projectGeographies: this.projectGeographies,
        selectedGeographies: this.selectedGeographies(),
        favouriteGeographies: this.favouriteGeographies,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.selectedGeographies) {
        this.selectedGeographies.set(result.selectedGeographies);
        this.store.selectedGeographies.set(result.selectedGeographies);
      }
    });
  }

  private fetchFavouriteGeographies(): void {
    const userId = this.getCurrentUserId();
    if (!userId) return;

    this.favouriteGeographyService.getFavouriteGeographies(userId).subscribe({
        next: (favourites) => {
            this.favouriteGeographies = favourites;
        },
        error: () => {
            this.favouriteGeographies = [];
        }
    });
  }

  private getCurrentUserId(): string | null {
    try {
        const userInfo = JSON.parse(localStorage.getItem('userInformation') ?? '');
        return userInfo?.id != null ? String(userInfo.id) : null;
    } catch {
        return null;
    }
  }
}
